<template>
   <div class="spark-inspector-container">
      <LoadingPage v-if="isLoading"></LoadingPage>

      <!-- Contract Version & Status Header -->
      <div class="spark-inspector-header">
         <div class="d-flex align-items-center justify-content-between w-100">
            <span class="spark-inspector-header__title">
               <i class="fa-solid fa-file-contract me-1 text-muted"></i> Contract Details
            </span>
            <span class="spark-pill-badge" :class="versionBadgeClass" :title="'Contract Version: ' + formattedVersion">
               {{ formattedVersion }}
            </span>
         </div>
      </div>

      <!-- 1. Commercials & Financials Card -->
      <div class="spark-inspector-card">
         <div class="spark-inspector-card__header">
            <span class="spark-inspector-card__title">
               <i class="fa-solid fa-coins me-2" style="color: #00A0C1;"></i> Financial Summary
            </span>
         </div>
         <div class="spark-inspector-card__body">
            <div class="spark-kpi-grid">
               <div class="spark-kpi-box">
                  <span class="spark-kpi-box__label">Total Value</span>
                  <span class="spark-kpi-box__value spark-kpi-box__value--primary">
                     {{ contract.total_amount || '$0.00' }}
                  </span>
               </div>
               <div class="spark-kpi-box">
                  <span class="spark-kpi-box__label">Accepted Value</span>
                  <span class="spark-kpi-box__value" :class="{'spark-kpi-box__value--success': contract.total_accepted && contract.total_accepted !== '$0.00' && contract.total_accepted !== '$0'}">
                     {{ contract.total_accepted || '$0.00' }}
                  </span>
               </div>
            </div>

            <!-- Discount Toggle Switch -->
            <div class="spark-toggle-row mt-2">
               <div class="spark-toggle-info">
                  <span class="spark-toggle-title">Show Discount Calculation</span>
                  <span class="spark-toggle-sub">Show discount breakdown on client portal</span>
               </div>
               <label class="spark-switch">
                  <input type="checkbox" v-model="contract.show_discount_calculation" :disabled="!canEditContract" @change="onToggleDiscountCalculation">
                  <span class="spark-switch-slider"></span>
               </label>
            </div>
         </div>
      </div>

      <!-- 2. Approval & Governance Card -->
      <div class="spark-inspector-card">
         <div class="spark-inspector-card__header">
            <span class="spark-inspector-card__title">
               <i class="fa-solid fa-signature me-2 text-primary"></i> Approval Status
            </span>
            <span v-if="contract.is_approved" class="spark-pill-badge spark-pill-badge--approved">
               <i class="fa-solid fa-check"></i> Approved
            </span>
            <span v-else-if="contract.approval_requested_to > 0" class="spark-pill-badge spark-pill-badge--pending-approval">
               <i class="fa-solid fa-clock-rotate-left"></i> Pending Approval
            </span>
            <span v-else class="spark-pill-badge spark-pill-badge--not-requested">
               <i class="fa-solid fa-circle-minus"></i> Not Requested
            </span>
         </div>
         <div class="spark-inspector-card__body">
            <div class="spark-meta-field" v-if="contract.approval_requested_to > 0">
               <span class="spark-meta-field__label">Requested Approver</span>
               <span class="spark-meta-field__value small">
                  <i class="fa-solid fa-user-check me-1 text-muted"></i>
                  {{ contract.approval_requested_to_email || getVertexAuthUser(contract.approval_requested_to) }}
               </span>
            </div>
            <div class="spark-meta-grid-2">
               <div class="spark-meta-field mb-0">
                  <span class="spark-meta-field__label">Approved By</span>
                  <span class="spark-meta-field__value small" :class="{'text-muted': !contract.is_approved}">
                     {{ contract.is_approved ? approvedByDisplay : 'Not yet approved' }}
                  </span>
               </div>
               <div class="spark-meta-field mb-0">
                  <span class="spark-meta-field__label">Approved Date</span>
                  <span class="spark-meta-field__value small" :class="{'text-muted': !contract.is_approved}">
                     {{ contract.is_approved ? (formatDate(contract.approved_at) || formatDate(contract.sent_at_raw || contract.sent_at) || '—') : '—' }}
                  </span>
               </div>
            </div>
         </div>
      </div>

      <!-- 3. Client & Organisation Card -->
      <div class="spark-inspector-card">
         <div class="spark-inspector-card__header">
            <span class="spark-inspector-card__title">
               <i class="fa-solid fa-building me-2 text-info"></i> Client & Organisation
            </span>
         </div>
         <div class="spark-inspector-card__body">
            <!-- Organisation Name -->
            <div class="spark-meta-field">
               <span class="spark-meta-field__label">Organisation</span>
               <div class="d-flex align-items-center justify-content-between flex-wrap gap-1">
                  <router-link v-if="organisationUuid"
                     :to="{ name: 'clients-dashboard', query: { organisation_uuid: organisationUuid } }"
                     class="spark-meta-field__value fw-bold text-primary text-decoration-none d-inline-flex align-items-center gap-1"
                     title="View Organisation">
                     <span>{{ contract.organisation_name || 'No Organisation' }}</span>
                     <i class="fa-solid fa-arrow-up-right-from-square small opacity-75"></i>
                  </router-link>
                  <span v-else class="spark-meta-field__value fw-bold text-dark">
                     {{ contract.organisation_name || 'No Organisation' }}
                  </span>
                  <span v-if="contract.original_organisation_name && contract.organisation_name !== contract.original_organisation_name" 
                     class="spark-pill-badge spark-pill-badge--draft" :title="'Original Lead Name: ' + contract.original_organisation_name">
                     <i class="fa-solid fa-history"></i> Formerly: {{ contract.original_organisation_name }}
                  </span>
               </div>
            </div>

            <!-- Business Number -->
            <div class="spark-meta-field">
               <span class="spark-meta-field__label">{{ businessNumberShortLabel }} (Business No.)</span>
               <div class="d-flex align-items-center justify-content-between">
                  <span class="spark-meta-field__value font-monospace" :class="{'text-muted': !contract.abn}">
                     {{ contract.abn || 'No Business Number' }}
                  </span>
                  <button v-if="contract.abn" type="button" class="spark-btn-copy" 
                     @click="copyToClipboard(contract.abn, businessNumberShortLabel)" title="Copy Business Number">
                     <i class="fa-regular fa-copy"></i>
                  </button>
               </div>
            </div>

            <!-- Client Contact -->
            <div class="spark-meta-field">
               <span class="spark-meta-field__label">Client Contact</span>
               <div class="spark-contact-pill">
                  <div class="spark-contact-avatar">
                     <i class="fa-solid fa-user"></i>
                  </div>
                  <div class="spark-contact-details">
                     <span class="spark-contact-name">{{ contract.client_lead_name || 'No Lead Name' }}</span>
                     <div class="d-flex align-items-center justify-content-between">
                        <a v-if="contract.client_lead_email" :href="'mailto:' + contract.client_lead_email" class="spark-contact-email" :title="'Email ' + contract.client_lead_email">
                           {{ contract.client_lead_email }}
                        </a>
                        <span v-else class="text-muted small">No email</span>
                        <button v-if="contract.client_lead_email" type="button" class="spark-btn-copy spark-btn-copy--sm" 
                           @click="copyToClipboard(contract.client_lead_email, 'Client Email')" title="Copy Email">
                           <i class="fa-regular fa-copy"></i>
                        </button>
                     </div>
                  </div>
               </div>
            </div>

            <!-- Contract Owner & Linked Contract ID -->
            <div class="spark-meta-grid-2">
               <div class="spark-meta-field mb-0">
                  <span class="spark-meta-field__label">Contract Owner</span>
                  <span class="spark-meta-field__value small">
                     <i class="fa-solid fa-user-tie me-1 text-muted"></i>
                     {{ contract.contract_owner || 'Unassigned' }}
                  </span>
               </div>
               <div class="spark-meta-field mb-0">
                  <span class="spark-meta-field__label">Linked Contract ID</span>
                  <span class="spark-meta-field__value small font-monospace" :class="{'text-muted': !contract.contract_id}">
                     {{ contract.contract_id || 'None' }}
                  </span>
               </div>
            </div>
         </div>
      </div>

      <!-- 4. Lifecycle & Timeline Card -->
      <div class="spark-inspector-card">
         <div class="spark-inspector-card__header">
            <span class="spark-inspector-card__title">
               <i class="fa-solid fa-timeline me-2 text-warning"></i> Lifecycle & Tracking
            </span>
         </div>
         <div class="spark-inspector-card__body">
            <div class="spark-timeline-list">
               <!-- Created -->
               <div class="spark-timeline-item">
                  <div class="spark-timeline-dot spark-timeline-dot--created"></div>
                  <div class="spark-timeline-content">
                     <span class="spark-timeline-label">Created Date</span>
                     <span class="spark-timeline-val">{{ contract.created_at || 'N/A' }}</span>
                  </div>
               </div>

               <!-- Internal Approval (if requested / required) -->
               <div v-if="contract.approval_requested_to > 0 || contract.contract_status === 6 || contract.contract_status === 7 || contract.contract_status === 8" class="spark-timeline-item">
                  <div class="spark-timeline-dot" 
                     :class="{
                        'spark-timeline-dot--accepted': contract.contract_status === 7 || contract.contract_status === 1 || contract.contract_status === 4,
                        'spark-timeline-dot--pending': contract.contract_status === 6,
                        'spark-timeline-dot--declined': contract.contract_status === 8
                     }"></div>
                  <div class="spark-timeline-content">
                     <div class="d-flex align-items-center justify-content-between">
                        <span class="spark-timeline-label">Internal Approval</span>
                        <span v-if="contract.contract_status === 6" class="spark-pill-badge spark-pill-badge--pending-approval">
                           <i class="fa-solid fa-clock-rotate-left me-1"></i> Pending
                        </span>
                        <span v-else-if="contract.contract_status === 7 || contract.contract_status === 1 || contract.contract_status === 4" class="spark-pill-badge spark-pill-badge--approved">
                           <i class="fa-solid fa-check me-1"></i> Approved
                        </span>
                        <span v-else-if="contract.contract_status === 8" class="spark-pill-badge spark-pill-badge--rejected">
                           <i class="fa-solid fa-xmark me-1"></i> Declined
                        </span>
                     </div>
                     <span v-if="contract.approval_requested_to_email" class="spark-timeline-val small text-muted">
                        Approver: {{ contract.approval_requested_to_email }}
                     </span>
                  </div>
               </div>

               <!-- Sent -->
               <div class="spark-timeline-item">
                  <div class="spark-timeline-dot" :class="contract.sent_at ? 'spark-timeline-dot--sent' : 'spark-timeline-dot--pending'"></div>
                  <div class="spark-timeline-content">
                     <div class="d-flex align-items-center justify-content-between">
                        <span class="spark-timeline-label">Sent Date</span>
                        <span v-if="contract.sent_at && sentTimeAgo" class="spark-pill-badge spark-pill-badge--sent" :title="'Timestamp: ' + contract.sent_at">
                           <i class="fa-regular fa-clock me-1"></i> {{ sentTimeAgo }}
                        </span>
                     </div>
                     <span class="spark-timeline-val" :class="{'text-muted': !contract.sent_at}">
                        {{ contract.sent_at || 'Not yet sent' }}
                     </span>
                  </div>
               </div>

               <!-- Expiry Date -->
               <div class="spark-timeline-item">
                  <div class="spark-timeline-dot spark-timeline-dot--expiry"></div>
                  <div class="spark-timeline-content">
                     <div class="d-flex align-items-center justify-content-between">
                        <span class="spark-timeline-label">Expiry Date</span>
                        <span v-if="contract.expiry_date && daysUntilExpiry !== null" 
                           class="spark-pill-badge" :class="expiryBadgeClass">
                           {{ expiryBadgeLabel }}
                        </span>
                     </div>
                     <span class="spark-timeline-val">{{ contract.expiry_date || 'No Expiry Set' }}</span>
                  </div>
               </div>

               <!-- Accepted / Executed (if applicable) -->
               <div v-if="contract.accepted_at || contract.contract_status === 4" class="spark-timeline-item">
                  <div class="spark-timeline-dot spark-timeline-dot--accepted"></div>
                  <div class="spark-timeline-content">
                     <span class="spark-timeline-label text-success">Accepted / Executed</span>
                     <span class="spark-timeline-val fw-bold text-success">{{ contract.accepted_at || 'Executed' }}</span>
                     <div v-if="contract.accepted_by_email" class="small text-muted mt-1">
                        <i class="fa-solid fa-check-circle text-success me-1"></i> {{ contract.accepted_by_email }}
                     </div>
                     <div v-if="contract.accepted_by_ip" class="small text-muted font-monospace">
                        <i class="fa-solid fa-shield-halved text-muted me-1"></i> IP: {{ contract.accepted_by_ip }}
                     </div>
                  </div>
               </div>
            </div>

            <!-- Shared Users -->
            <div class="spark-meta-field mt-2 mb-0 pt-2 border-top">
               <span class="spark-meta-field__label d-flex align-items-center justify-content-between">
                  <span>Shared With</span>
                  <span class="badge bg-light text-muted">{{ (contract.shared_users && contract.shared_users.length) || 0 }}</span>
               </span>
               <div v-if="contract.shared_users && contract.shared_users.length > 0" class="spark-shared-list">
                  <div v-for="share in contract.shared_users" :key="share.email" class="spark-shared-chip">
                     <span class="spark-shared-email"><i class="fa-solid fa-user-group me-1 text-muted"></i>{{ share.email }}</span>
                     <span class="spark-shared-role">{{ share.share_type }}</span>
                  </div>
               </div>
               <p v-else class="text-muted small mb-0 mt-1">
                  <i class="fa-solid fa-lock me-1"></i> Not shared with anyone yet.
               </p>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import moment from "moment";
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
//  import { isEmpty } from '@/plugins/helper.js'
//  import 'vue-tel-input/dist/vue-tel-input.css'
//  import VueMultiselect from 'vue-multiselect'
//  import 'vue-multiselect/dist/vue-multiselect.css'
//  import "@/assets/scss/vue-multiselect.scss";
import LoadingPage from '@/components/Utilities/vertex-loading.vue'
import { nextTick } from 'vue'; // Import nextTick
import { debounce, formatCurrency, log, isEmpty } from '@/plugins/helper.js'
import { getBusinessNumberShortLabel } from '@/plugins/businessValidator.js'

//const _ = require('lodash');

export default {

   components: {
      //    VueMultiselect,
      LoadingPage,
   },

   setup() {
      return { v$: useVuelidate() }
   },

   emits: ['onSubmit', 'onDelete', 'onInitializeDetails'],

   props: {
      item: {
         type: Object,
         // required: true,
      },
      id: {
         type: String,
      },
   },

   data() {
      return {
         currentDate: null,
         contract: {
            created_date: null,
            organisation_name: "",
            abn: "",
            original_organisation_name: "",
            original_abn: "",
            contract_id: null,
            contract_owner: null,
            contract_title: "",
            client_lead_name: "",
            client_lead_email: "",
            total_amount: 0.00,
            total_accepted: 0.00,
            contract_version: "",
            created_at: null,
            expiry_date: null,
            sent_at: null,
            sent_at_raw: null,
            accepted_at: null,
            content: "",
            contract_items: [],
            contract_status: 0,
            shared_users: [],
            accepted_by_email: null,
            accepted_by_ip: null,
            option_index_selected: null,
            is_approved: false,
            approved_by: null,
            approved_at: null,
            approval_requested_by: 0,
            approval_requested_to: 0,
            approval_requested_to_email: '',
            has_revision_not_sent: false,
         },
         productList: [],
         isEditing: false,
         disabledWhenClicked: false,
         isLoading: false,
         debounceFn: null,
         currentBlockType: "none",
         currentContractName: "none",
         lead: null,
         ShowDiscountChanged: false,
         ShowDiscountCurrentValue: false,
         vtxUsers: [],
      }
   },

   validations() {
      return {
         block: {
            blockName: { required },
            blockType: { required },
            blockUse: { required },
         },
      }
   },

    computed: {
      organisationUuid() {
         return this.lead?.organisation_uuid ||
                this.lead?.auth_organisation_uuid ||
                this.lead?.organisation?.uuid ||
                this.contract?.organisation_uuid ||
                '';
      },
      businessNumberShortLabel() {
         const code = this.lead?.country_code || this.lead?.country || this.contract?.country_code || this.contract?.country || this.lead?.organisation?.country || 'AU';
         return getBusinessNumberShortLabel(code);
      },
      daysUntilExpiry() {
         if (!this.contract.expiry_date) return null;
         return this.DaysToAcceptCounter(this.contract.expiry_date);
      },
      expiryBadgeClass() {
         if (this.daysUntilExpiry === null) return '';
         if (this.daysUntilExpiry < 0) return 'spark-pill-badge--expired';
         if (this.daysUntilExpiry <= 3) return 'spark-pill-badge--priority-high';
         return 'spark-pill-badge--priority-low';
      },
      expiryBadgeLabel() {
         if (this.daysUntilExpiry === null) return '';
         if (this.daysUntilExpiry < 0) return 'Expired';
         if (this.daysUntilExpiry === 0) return 'Expires today';
         return `${this.daysUntilExpiry} days left`;
      },
      sentTimeAgo() {
         const dateVal = this.contract.sent_at_raw || this.contract.sent_at;
         if (!dateVal || !moment(dateVal).isValid()) return '';
         return moment(dateVal).fromNow();
      },
      canEditContract() {
         // Lock editing if the contract is locked (pending approval, approved, or executed)
         if (this.contract.contract_status === 4 || this.contract.contract_status === 6 || this.contract.contract_status === 7) {
            return false;
         }
         // Global Super Admin can edit any contract
         if (this.$store.getters['auth/isGlobalSuperAdmin']) {
            return true;
         }
         // Admin Support / Super Admin can edit any contract
         if (this.$store.state.auth.isAdmin || this.$store.state.auth.isSuperAdmin) {
            return true;
         }
         // Spark Consultant can edit if they own the lead/contract
         if (this.$store.state.auth.isConsultant) {
            const currentUserId = this.$store.state.auth?.currentUser?.id || 0;
            const creatorId = this.lead ? this.lead.created_by : 0;
            return creatorId > 0 && currentUserId === creatorId;
         }
         return !this.$store.state.auth.isGeneralUser;
      },
      approvedByDisplay() {
         if (!this.contract.is_approved) return 'Not yet approved';
         if (!this.contract.approved_by || this.contract.approved_by === 0 || this.contract.approved_by === '0') {
            return 'System';
         }
         const userName = this.getVertexAuthUser(this.contract.approved_by);
         if (!userName || userName === 'Deleted User' || userName === 'System Generated') {
            return 'System';
         }
         return userName;
      },
      versionBadgeClass() {
         const version = this.contract?.contract_version || 'v1.0';
         const clean = version.startsWith('v') ? version.slice(1) : version;
         const parts = clean.split('.');
         if (parts.length >= 2) {
            const minor = parseInt(parts[1], 10);
            if (!isNaN(minor)) {
               return minor > 0 ? 'spark-pill-badge--version-minor' : 'spark-pill-badge--version-major';
            }
         }
         return 'spark-pill-badge--version-major';
      },
      formattedVersion() {
         const version = this.contract?.contract_version || 'v1.0';
         return version.startsWith('v') ? version : `v${version}`;
      },
   },

   watch: {
      currentBlockType() {
         if (this.currentContractName !== "none") {
            this.setHeaderTitle()
         }
      }
   },

   created() {
      this.init()
      this.debounceFn = debounce(() =>
         this.setContentIfNotEmpty()
         , 2500)
      this.setHeaderTitle()
   },

   mounted() {
      nextTick(() => {
         this.$parent.setContent(this.contract.content, false)
      })
   },

   methods: {
      async copyToClipboard(text, label = "Item") {
         if (!text || text === 'N/A' || text === 'None' || text === 'No Business Number') return;
         try {
            if (navigator.clipboard && window.isSecureContext) {
               await navigator.clipboard.writeText(text);
            } else {
               const textArea = document.createElement("textarea");
               textArea.value = text;
               textArea.style.position = "fixed";
               textArea.style.left = "-999999px";
               document.body.appendChild(textArea);
               textArea.focus();
               textArea.select();
               document.execCommand("copy");
               textArea.remove();
            }
            this.$toast.success(`${label} copied to clipboard`);
         } catch (err) {
            console.error("Failed to copy:", err);
            this.$toast.error("Failed to copy to clipboard");
         }
      },
      init() {
         this.getVertexUsers()
         this.currentDate = moment(new Date()).format("DD-MMM-YYYY");
      },


      formatDate(date) {
         if (!date || !moment(date).isValid() || moment(date).year() < 1980) {
            return null;
         }
         return moment(date).format("DD-MMM-YYYY @ HH:mm:ss");
      },

      formatDateNoTime(date) {
         if (!date || !moment(date).isValid() || moment(date).year() < 1980) {
            return null;
         }
         return moment(date).format("DD-MMM-YYYY");
      },

      formatUtcToTimeZone(utcDateString) {
         // 1. Parse the UTC date string into a Date object.
         // JavaScript Date objects internally represent time as milliseconds since the Unix epoch,
         // which is timezone-agnostic (effectively UTC).
         const date = new Date(utcDateString);

         // 2. Define formatting options.
         // 'timeZone' option tells Intl.DateTimeFormat to convert the date to this specific time zone.
         const options = {
            day: '2-digit',   // DD (e.g., 01, 15, 31)
            month: 'short',  // MMM (e.g., Jan, Feb, Dec)
            year: 'numeric',  // YYYY (e.g., 2023, 2025)
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            // Optional: timeZoneName: 'short' or 'long' if you want to include the time zone name
         };

         // 3. Create an Intl.DateTimeFormat instance and format the date.
         return new Intl.DateTimeFormat('en-US', options).format(date);
      },

      formatToTimestamp(date) {
         const day = date.getDate();
         const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
         const month = monthNames[date.getMonth()];
         const year = date.getFullYear();
         const hours = date.getHours().toString().padStart(2, '0');
         const minutes = date.getMinutes().toString().padStart(2, '0');
         const seconds = date.getSeconds().toString().padStart(2, '0');
         const formattedDate = `${day}-${month}-${year} @ ${hours}:${minutes}:${seconds}`;

         if (year < 1980) {
            return null;
         }

         return formattedDate;
      },

      /**
* Calculates the number of calendar days between the current date (now) and an expiry date.
* The calculation considers full calendar days, meaning it normalizes both dates to the
* start of their respective days in the user's local timezone.
*
* @param {string} utcDateString The expiry date string in UTC format (e.g., "2025-06-08T05:00:00Z").
* @returns {number} The difference in full calendar days.
* Positive if the expiry date is in the future, negative if in the past.
* 0 if it's the same calendar day.
*/
      DaysToAcceptCounter(utcDateString) {
         // 1. Get the expiry date as a Date object.
         //    It's parsed from a UTC string, but the Date object itself stores a universal timestamp.
         const expiryDate = new Date(utcDateString);

         // 2. Get the current date and time in the user's local timezone.
         const now = new Date();

         // 3. To accurately count "calendar days" (days on the calendar, ignoring time of day),
         //    we need to determine the start of the day for both 'now' and 'expiryDate'
         //    in the *user's local timezone*.

         //    Create a new Date object representing the start of the current day (00:00:00)
         //    in the user's local timezone. By passing year, month, and day to the Date constructor,
         //    it automatically creates a date in the local timezone at midnight.
         const startOfTodayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());

         //    Create a new Date object representing the start of the expiry day (00:00:00)
         //    in the user's local timezone.
         //    We extract the year, month, and date components from the 'expiryDate' using its local getters
         //    (e.g., `getFullYear()`, `getMonth()`, `getDate()`). These getters automatically
         //    convert the internal UTC timestamp to the corresponding local calendar day.
         //    Then, creating a new Date object with these local components ensures we get the
         //    midnight of that specific local calendar day.
         const startOfExpiryDayLocal = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());

         // 4. Calculate the difference in milliseconds between these two "start-of-day" timestamps.
         const differenceInMs = startOfExpiryDayLocal.getTime() - startOfTodayLocal.getTime();

         // 5. Convert the millisecond difference to days.
         //    We divide by the number of milliseconds in a standard day (24 hours).
         const millisecondsPerDay = 1000 * 60 * 60 * 24;
         let daysDifference = differenceInMs / millisecondsPerDay;

         // 6. Round the result to the nearest whole number.
         //    Using Math.round() helps in handling any minor floating-point inaccuracies or
         //    cases where a "day" might be slightly more or less than 24 hours due to
         //    Daylight Saving Time (DST) transitions. This ensures we get clear whole calendar day counts.
         return Math.round(daysDifference);
      },

      editContract(id) {
         this.getContract(id)
      },

      triggerDebounce() {
         this.debounceFn()
      },

      async setContentIfNotEmpty() {
         this.isLoading = true
         setTimeout(() => {
            this.isLoading = false 
            //this.toggleIsLoading()
            if (this.contract.content != undefined && this.contract.content != null) {
               this.$parent.setContent(this.contract.content, false)
            }
         }, 2500)
      },

      async setHeaderTitle() {
         this.$parent.setHeaderTitle(this.currentContractName)
      },

      toggleIsLoading() {
         this.isLoading = !this.isLoading
      },

      getInternalAccessKey() {
         return this.contract.internal_key
      },

      getUniqueKey() {
         return this.contract.unique_key
      },

      async getContract(id) {
         this.isLoading = true;

         await this.$axios.get("/contracts/" + id)
            .then((response) => {
               let resp = response.data.vertex_contract
               this.contract.id = resp.id
               this.contract.unique_key = resp.unique_key
               this.contract.internal_key = resp.internal_key
               this.contract.content = resp.content
               this.contract.created_date = resp.created_at
               let acceptedOrgName = ""
               let acceptedAbn = ""
               if (resp.contract_acceptance) {
                  acceptedOrgName = resp.contract_acceptance.organisation_name
                  acceptedAbn = resp.contract_acceptance.abn
               }
               let leadOrgName = resp.Lead ? (resp.Lead.auth_organisation_name || resp.Lead.organisation_name) : ""
               let leadAbn = resp.Lead ? resp.Lead.abn : ""
                let originalLeadOrgName = resp.Lead ? (resp.Lead.original_organisation_name || "") : ""
                let originalLeadAbn = resp.Lead ? (resp.Lead.original_abn || "") : ""

               this.contract.organisation_name = acceptedOrgName || leadOrgName
               this.contract.abn = acceptedAbn || leadAbn
               this.contract.original_organisation_name = originalLeadOrgName
               this.contract.original_abn = originalLeadAbn
               this.contract.contract_id = resp.contract_id
               this.contract.contract_owner = this.getVertexAuthUser(resp.created_by)
               this.contract.contract_title = resp.contract_title
               this.contract.client_lead_name = resp.Lead.first_name + " " + resp.Lead.last_name
               this.contract.client_lead_email = resp.Lead.email
               this.contract.total_amount = formatCurrency(resp.contract_total_amount)
               this.contract.total_accepted = formatCurrency(resp.contract_accepted_amount)
               this.contract.expiry_date = (resp.expiry_date != null) ? this.formatDateNoTime(resp.expiry_date) : null
               this.contract.contract_version = resp.contract_version
               this.contract.created_at = this.formatDate(resp.created_at)
               this.contract.sent_at = (resp.sent_at != null) ? this.formatDate(resp.sent_at) : null
               this.contract.sent_at_raw = resp.sent_at || null
               this.contract.accepted_at = (resp.accepted_at != null) ? this.formatDate(resp.accepted_at) : null
               this.contract.contract_status = (resp.contract_status != null && resp.contract_status != undefined) ? resp.contract_status : null 
               this.contract.show_discount_calculation = resp.show_discount_calculation
               this.contract.contract_section_content = resp.contract_section_content
               this.ShowDiscountCurrentValue = resp.show_discount_calculation
               this.lead = resp.Lead
               this.currentContractName = this.contract.contract_title
               this.setHeaderTitle()
               this.contract.contract_items = resp.contract_items || resp.ContractItems || []
               this.contract.shared_users = resp.shared_users || []
               this.contract.accepted_by_email = resp.accepted_by_email || null
               this.contract.accepted_by_ip = resp.accepted_by_ip || null
               this.contract.option_index_selected = resp.option_index_selected || null
               this.contract.is_approved = resp.is_approved || false
               this.contract.approved_by = resp.approved_by || null
               this.contract.approved_at = resp.approved_at || resp.sent_at || null
               this.contract.approval_requested_by = resp.approval_requested_by || 0
               this.contract.approval_requested_to = resp.approval_requested_to || 0
               this.contract.approval_requested_to_email = resp.approval_requested_to_email || ''
               this.contract.has_revision_not_sent = resp.has_revision_not_sent || false
               this.$emit('onInitializeDetails')
               this.isLoading = false;
            }).catch((error) => {
               this.$toast.error(error)
               this.isLoading = false;
            })
      },

      getVersion() {
         return this.contract.contract_version
      },

      getContractItems() {
         const rawItems = this.contract.contract_items || this.contract.ContractItems || []
         if (!isEmpty(rawItems)) {
            return rawItems.map((prop_item, idx) => {
               const isHeading = !!(prop_item.is_heading || prop_item.isHeading || prop_item.IsHeading);
               return {
                  ...prop_item,
                  id: prop_item.id || prop_item.product_id,
                  product_id: prop_item.product_id ? prop_item.product_id.toString() : (prop_item.ProductID ? prop_item.ProductID.toString() : (prop_item.id ? prop_item.id.toString() : "")),
                  product_name: (prop_item.product_name && prop_item.product_name.trim()) ? prop_item.product_name : (prop_item.ProductName || prop_item.product || prop_item.Product?.display_name || prop_item.Product?.name || (isHeading ? "Section Heading" : "Product")),
                  description: (prop_item.description !== undefined && prop_item.description !== null && String(prop_item.description).trim() !== "") ? prop_item.description : (prop_item.Description || prop_item.Product?.description || ""),
                  remarks: prop_item.remarks || prop_item.Remarks || "",
                  price: isHeading ? 0 : (prop_item.price !== undefined && prop_item.price !== null ? prop_item.price : (prop_item.Price !== undefined ? prop_item.Price : (prop_item.pricing || 0))),
                  quantity: isHeading ? 0 : (prop_item.quantity !== undefined && prop_item.quantity !== null ? prop_item.quantity : (prop_item.Quantity !== undefined ? prop_item.Quantity : 1)),
                  discount: isHeading ? 0 : (prop_item.discount !== undefined && prop_item.discount !== null ? prop_item.discount : (prop_item.Discount !== undefined ? prop_item.Discount : 0)),
                  is_heading: isHeading,
                  option_index: prop_item.option_index || prop_item.OptionIndex || 1,
                  line_item_index: prop_item.line_item_index || prop_item.LineItemIndex || (idx + 1),
                  is_charity: this.lead ? this.lead.is_charity : false
               };
            });
         }

         return []
      },

         // let pitemsarray = []
         // if(!isEmpty(this.contract.contract_items))
         // this.contract.contract_items.forEach(pitem =>{
         //    pitemsarray.push({
         //       description: pitem.description,
         //       discount: pitem.discount,
         //       id: pitem.id,
         //       price: pitem.price,
         //       product_id: pitem.product_id,
         //       contract_id: pitem.contract_id,
         //       quantity: pitem.quantity
         //    })
         // })

         // return pitemsarray

      isShowDiscountChanged() {
         return this.ShowDiscountCurrentValue == this.contract.show_discount_calculation
      },

      onToggleDiscountCalculation() {
         this.$emit('toggleDiscountCalculation', this.contract.show_discount_calculation);
      },


      async updateContract(contract_section_content) {

         log("contract_id", this.contract.id)

         contract_section_content["show_discount_calculation"] = this.contract.show_discount_calculation

         return await this.$axios.put("/contracts/" + this.contract.id + "/update-content", contract_section_content)
            .then((response) => {
               if (response.data.success) {
                  this.isLoading = true
                  this.editContract(this.contract.id)
                  this.isLoading = false
                  this.$parent.clearIsDirty()
                  //this.ShowDiscountChanged = false
                  this.$toast.success(response.data.message)
                  return true;
               } else {
                  this.isLoading = false
                  this.$toast.error(response.data.message)
                  return false;
               }
            })
            .catch((error) => {
               this.isLoading = false
               console.error(error)
               this.$toast.error(error.response?.data?.message || error.message || "Failed to update contract")
               return false;
            })
      },

      async sendContract() {

         log("contract_id", this.contract.id)

         await this.$axios.put("/contracts/" + this.contract.id + "/send")
            .then((response) => {
               if (response.data.success) {
                  this.isLoading = true
                  this.editContract(this.contract.id)
                  this.isLoading = false
                  this.$toast.success(response.data.message)
               } else {
                  this.isLoading = false
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               this.isLoading = false
               console.error(error)
               this.$toast.error(error.response.data.message)
            })
      },

      getVertexAuthUser(id) {
         if (!id || id === 0 || id === '0') {
            return "System"
         }

         // Static fallback user while auth is being migrated
         const staticUsers = [
            { id: 1, first_name: 'Richie', last_name: 'Golez' }
         ];

         let user = Array.isArray(this.vtxUsers) ? this.vtxUsers.find(user => user && user.id === id) : null;
         if (user) {
            return user.first_name + " " + user.last_name
         }

         // Check static users as fallback
         let staticUser = staticUsers.find(u => u && u.id === id);
         if (staticUser) {
            return staticUser.first_name + " " + staticUser.last_name
         }

         return "Deleted User"
      },

      getVertexUsers() {
          this.vtxUsers = this.$store.state.auth.usersWithVertexRoles || []
         // await this.$axios_auth.get("/supadmin/users")
         //    .then((response) => {
         //       sortAlphabetically(response.data.users, (item) => item.user.first_name)
         //       this.vtxUsers = response.data.users
         //       //this.$store.commit("auth/setUsersWithVertexRoles", response.data.users)
         //    })
         //    .catch((error) => {
         //       console.error(error)
         //       // this.$toast.error(error.response.data.message)
         //    })
      },

      resetForm() {
         this.contract = null
      },
   },

};

</script>

<style scoped>
.version-status-icon {
   display: inline-flex;
   justify-content: center;
   align-items: center;
   min-width: 45px;
   height: 24px;
   padding: 0 8px;
   border-radius: 6px;
   color: white;
   font-size: 12px;
   font-weight: bold;
   text-transform: none;
   white-space: nowrap;
   vertical-align: middle;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.high {
   color: #EB1C24;
}

.low {
   color: #00A54F;
}

.medium {
   color: #F3994D;
}

select>option {
   color: #435666;
   /* border: 1px solid #D9D9D9; */
   /* border-radius: 5px; */
}

.btn {
   width: 7rem;
}

form label:not(#isCharityLbl) {
   color: #00A0C1;
   margin: 0.5rem 0;
}

input:not([type="checkbox"]),
select {
   height: 40px;
   border-radius: 10px;
   border: 1px solid #D9D9D9;
   color: #435666;
}

.form--check-label {
   /* Adjust spacing between checkbox and label */
   color: #495057 !important;
   font-size: 15px;
   align-items: center;
   /* Vertically center the content */
   justify-content: center;
   /* Horizontally center the content */
   /* Grayish color for the label */
}

.form--check-input {
   width: 12px;
   /* Increase width */
   height: 12px;
   /* Increase height */
   margin-left: -17px;
   margin-right: 10px;
}

.add-space {
   margin-bottom: 225px;
}

.btn-circle-color {
   width: 40px;
   height: 40px;
   padding: 6px 0px;
   border-radius: 15px;
   text-align: center;
   font-size: 12px;
   line-height: 1.42857;
   border: 1px solid #00A0C1;
   color: #00A0C1;
}

.status-container {
   display: grid;
   grid-template-columns: auto auto;
   /* Creates two equal width columns */
   gap: 10px;
   /* Adds some space between the grid items */
   padding: 15px;
   background-color: #f7f7f7;
   /* Light background */
   border-radius: 8px;
   /* Rounded corners */
}

.status-item {
   display: flex;
   flex-direction: column;
   padding: 10px;
   background-color: white;
   /* Slightly darker background for items */
   border-radius: 5px;
}

.full-width {
   grid-column: 1 / -1;
   /* Make this item span both columns */
}

.status-label {
   font-size: 0.85em;
   color: #555;
   margin-bottom: 5px;
}

.status-value {
   font-weight: bold;
   color: #333;
}

.acceptance-info {
   grid-column: 1 / -1;
   /* Span both columns */
   display: flex;
   gap: 10px;
   margin-top: 10px;
}

.acceptance-item {
   display: flex;
   flex-direction: column;
   flex: 1;
   /* Distribute available space equally */
   padding: 10px;
   background-color: white;
   border-radius: 5px;
   text-align: center;
   /* Center the content */
}

.acceptance-label {
   font-size: 0.8em;
   color: #555;
   margin-bottom: 5px;
}

.acceptance-value {
   font-weight: bold;
}

.yes {
   color: green;
}

.no {
   color: orange;
}

.checkbox-label {
  display: inline-flex;
  /* Changed to inline-flex for better alignment in the cell */
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

/* Hide the default browser checkbox */
.styled-checkbox {
  display: none;
}

/* The custom checkbox container */
.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #00bcd4;
  border-radius: 4px;
  margin-right: 12px;
  display: inline-block;
  position: relative;
  transition: background-color 0.2s, border-color 0.2s;
}

/* Create the checkmark/tick using a pseudo-element */
.checkbox-custom::after {
  content: '';
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

/* Style the custom checkbox when checked */
.styled-checkbox:checked+.checkbox-custom {
  background-color: #00bcd4;
  border-color: #00bcd4;
}

/* Show the checkmark when the checkbox is checked */
.styled-checkbox:checked+.checkbox-custom::after {
  display: block;
}

/* START: UPDATED CSS FOR "SHARED WITH" SECTION */
.shared-users-list {
  margin-top: 5px;
}

.shared-user-item-inline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.3rem 0;
    /* Add a border for separation if you have multiple users */
    border-bottom: 1px solid #f0f0f0;
}

.shared-user-item-inline:last-child {
   border-bottom: none; /* Remove border from the last item */
}

.shared-user-email {
    color: #435666;
    font-size: 0.9em;
}

.share-type-badge {
    background-color: #e9ecef;
    color: #495057;
    padding: 0.25rem 0.75rem;
    border-radius: 10px;
    font-size: 0.8em;
    font-weight: bold;
    text-transform: capitalize;
}


.form-control-static-container {
  min-height: 40px;
  padding: 0.6rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #435666;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 10px;
  opacity: 1;
}

.shared-users-list {
  margin-top: 0;
}

</style>
